<template>
  <v-container fluid class="promotion-form-page">
    <!-- PAGE HEADER -->
    <div class="ap-header-row mb-5 d-flex align-center justify-space-between flex-wrap" style="gap: 16px;">
      <div class="d-flex align-center" style="gap: 12px;">
        <v-btn
          depressed
          outlined
          class="ap-back-btn rounded-lg"
          @click="goBack"
        >
          <v-icon small color="primary">fal fa-arrow-left</v-icon>
        </v-btn>
        <div>
          <div class="d-flex align-center" style="gap: 8px;">
            <h1 class="promotion-form-title font-weight-black mb-0">
              {{ isEdit ? $t("edit_promotion") : $t("create_promotion") }}
            </h1>
            <v-chip
              small
              label
              :color="form.is_active ? 'success' : 'grey'"
              class="font-weight-bold white--text"
            >
              {{ form.is_active ? $t("active") : $t("inactive") }}
            </v-chip>
          </div>
          <div class="text-caption grey--text mt-1">
            {{ isEdit ? $t("edit_promotion_subtitle") || "Update promotion rules, targets, and dates" : $t("create_promotion_subtitle") || "Configure discount rules, vouchers, and eligibility" }}
          </div>
        </div>
      </div>

      <!-- Quick top actions on desktop -->
      <div class="d-none d-sm-flex align-center" style="gap: 10px;">
        <v-btn
          depressed
          outlined
          class="text-none rounded-lg font-weight-bold px-4"
          @click="goBack"
        >
          {{ $t("cancel") }}
        </v-btn>
        <v-btn
          v-if="isEdit"
          depressed
          outlined
          color="error"
          class="text-none rounded-lg font-weight-bold px-4"
          @click="deletePromotion"
        >
          <v-icon left small>fal fa-trash-alt</v-icon>
          {{ $t("delete_promotion") }}
        </v-btn>
        <v-btn
          depressed
          color="primary"
          class="text-none rounded-lg font-weight-bold px-5 white--text"
          :loading="submitting"
          @click="submitForm"
        >
          <v-icon left small>{{ isEdit ? "fal fa-save" : "fal fa-check" }}</v-icon>
          {{ isEdit ? $t("update_promotion") : $t("create_promotion") }}
        </v-btn>
      </div>
    </div>

    <!-- MAIN FORM GRID (8 COLS MAIN + 4 COLS SIDEBAR) -->
    <v-row v-if="!pageLoading">
      <!-- ============ MAIN COLUMN (8 COLS) ============ -->
      <v-col cols="12" lg="8" class="pr-lg-3">
        <!-- 1. GENERAL INFORMATION -->
        <v-card class="ap-card mb-4" outlined elevation="0">
          <v-card-title class="py-3 px-4 d-flex align-center border-bottom ap-card-title">
            <span class="ap-card-accent mr-2"></span>
            <span class="font-weight-bold text-subtitle-2">{{ $t("general_information") || "General Information" }}</span>
          </v-card-title>
          <v-card-text class="pa-4 pa-md-5">
            <div class="form-grid mb-4">
              <div ref="field_name">
                <div class="section-label">
                  {{ $t("promotion_name") }} <span class="required-mark">*</span>
                </div>
                <TTextField
                  label=""
                  :placeholder="$t('enter_promotion_name')"
                  :maxlength="150"
                  :counter="true"
                  :errorMessages="fieldError('name')"
                  v-model="form.name"
                />
              </div>

              <div ref="field_code">
                <div class="section-label">
                  {{ $t("promotion_code") }} <span class="required-mark">*</span>
                </div>
                <TTextField
                  label=""
                  :placeholder="$t('enter_promotion_code')"
                  :maxlength="15"
                  :counter="true"
                  :errorMessages="fieldError('code')"
                  v-model="form.code"
                />
              </div>
            </div>

            <div>
              <div class="section-label">{{ $t("note_optional") }}</div>
              <TextArea
                label=""
                :aria-label="$t('note_optional')"
                placeholder="describe_promotion_placeholder"
                :rows="3"
                :errorMessages="fieldError('note')"
                v-model="form.note"
              />
            </div>
          </v-card-text>
        </v-card>

        <!-- 2. PROMOTION RULE & DISCOUNTS -->
        <v-card class="ap-card mb-4" outlined elevation="0">
          <v-card-title class="py-3 px-4 d-flex align-center border-bottom ap-card-title">
            <span class="ap-card-accent mr-2"></span>
            <span class="font-weight-bold text-subtitle-2">{{ $t("discount_rule") || "Discount Rule & Criteria" }}</span>
          </v-card-title>
          <v-card-text class="pa-4 pa-md-5">
            <div class="mb-4">
              <div class="section-label">{{ $t("promotion_type") }}</div>
              <div class="promotion-type-grid">
                <button
                  v-for="option in promotionTypeOptions"
                  :key="option.value"
                  type="button"
                  class="promotion-type-card"
                  :class="{ active: form.promotion_type === option.value }"
                  @click="handlePromotionTypeChange(option.value)"
                >
                  <div class="promotion-type-icon-wrap">
                    <v-icon class="promotion-type-icon" size="20">
                      {{ option.icon }}
                    </v-icon>
                  </div>
                  <div class="promotion-type-text">
                    <div class="promotion-type-title">{{ $t(option.value) }}</div>
                    <div class="promotion-type-subtitle">
                      {{ $t(option.descKey) }}
                    </div>
                  </div>
                  <v-icon
                    v-if="form.promotion_type === option.value"
                    class="promotion-type-check"
                    small
                  >
                    fal fa-check-circle
                  </v-icon>
                </button>
              </div>
            </div>

            <!-- Discount Value (If not buyx_gety) -->
            <div v-if="form.promotion_type !== 'buyx_gety'" ref="field_discount_value" class="section-block">
              <div class="section-label">
                {{ $t("discount_value") }} <span class="required-mark">*</span>
              </div>
              <div class="discount-value-row">
                <v-text-field
                  v-if="form.discount_type === 'percent'"
                  :value="percentDisplayValue"
                  @input="handlePercentInput"
                  @keydown="blockNonDigitKey"
                  @paste="handlePercentPaste"
                  type="text"
                  inputmode="numeric"
                  maxlength="3"
                  placeholder="10%"
                  solo
                  flat
                  dense
                  hide-details
                  class="discount-value-input"
                />
                <CurrencyInput
                  v-else
                  v-model="form.discount_value"
                  label=""
                  placeholder="10,000"
                  hide-details
                  class="discount-value-input discount-value-currency"
                />
                <div class="discount-divider" />
                <v-select
                  v-model="form.discount_type"
                  :items="discountTypeItems"
                  solo
                  flat
                  dense
                  hide-details
                  class="discount-type-select"
                >
                  <template #item="{ item, on, attrs }">
                    <v-list-item v-bind="attrs" v-on="on">
                      <v-list-item-icon class="discount-type-check-icon">
                        <v-icon v-if="form.discount_type === item.value" small color="primary">
                          fal fa-check
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>{{ item.text }}</v-list-item-title>
                    </v-list-item>
                  </template>
                </v-select>
              </div>
              <div v-if="fieldError('discount_value').length" class="error-text">
                {{ fieldError("discount_value")[0] }}
              </div>
            </div>

            <!-- Participants Selection (If not buyx_gety) -->
            <div v-if="form.promotion_type !== 'buyx_gety'" ref="field_participants" class="section-block">
              <div class="section-label">{{ $t("participant_selection") }}</div>
              <div class="participant-box">
                <div class="participant-targets">
                  <v-icon small color="primary" class="mr-2">fal fa-tag</v-icon>
                  <span class="participant-target-label">{{ targetTypeLabel }}</span>
                </div>

                <v-btn
                  v-if="form.target_type !== 'all'"
                  depressed
                  outlined
                  color="primary"
                  class="rounded-lg text-none mt-3 participant-select-btn"
                  @click="participantDialog = true"
                >
                  <v-icon left small>fal fa-users</v-icon>
                  {{ participantButtonLabel }}
                </v-btn>

                <div
                  v-if="participantError.length"
                  class="error-text mt-2"
                >
                  {{ participantError[0] }}
                </div>

                <div v-if="participantSummaryItems.length" class="selected-chips">
                  <v-chip
                    v-for="item in participantSummaryItems"
                    :key="`${form.target_type}-${item.id}`"
                    small
                    label
                    close
                    close-icon="fal fa-times"
                    class="mr-2 mb-2"
                    @click:close="removeParticipantItem(item)"
                  >
                    {{ item.label }}
                  </v-chip>
                </div>
              </div>
            </div>

            <!-- Buy X Get Y Configuration -->
            <div v-else ref="field_buy_items" class="section-block">
              <div class="used-tabs-row mb-3 d-flex align-center justify-space-between flex-wrap">
                <div class="d-flex align-center">
                  <span class="section-label mb-0 mr-3">{{ $t("condition_mode") || "Condition Match" }}:</span>
                  <div class="br-tabs">
                    <div
                      v-for="option in usedOptions"
                      :key="option.value"
                      class="br-tab"
                      :class="{ 'br-tab--active': form.used === option.value }"
                      @click="form.used = option.value"
                    >
                      {{ $t(option.labelKey) }}
                    </div>
                  </div>
                </div>

                <v-tooltip bottom max-width="320">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      small
                      color="grey lighten-1"
                      class="ml-1"
                      v-bind="attrs"
                      v-on="on"
                    >
                      fal fa-question-circle
                    </v-icon>
                  </template>
                  <span style="white-space: pre-line">{{ $t("promotion_used_hint") }}</span>
                </v-tooltip>
              </div>

              <!-- Buy Products Section -->
              <div class="mb-4">
                <div class="section-label">{{ $t("buy_products") }}</div>
                <div class="participant-box">
                  <v-btn
                    depressed
                    outlined
                    color="primary"
                    class="rounded-lg text-none participant-select-btn"
                    @click="buyDialog = true"
                  >
                    <v-icon left small>fal fa-cart-plus</v-icon>
                    {{ $t("select_products") }}
                  </v-btn>

                  <div v-if="fieldError('buy_items').length" class="error-text mt-2">
                    {{ fieldError("buy_items")[0] }}
                  </div>

                  <div v-if="form.buy_items.length" class="item-qty-list mt-3">
                    <div
                      v-for="item in form.buy_items"
                      :key="`buy-${item.product_id}`"
                      class="item-qty-row"
                    >
                      <div class="item-qty-label-col">
                        <span class="item-qty-label font-weight-medium">{{ item.label }}</span>
                        <div v-if="item.variantLabels && item.variantLabels.length" class="item-variant-chips">
                          <span class="item-variant-chips-title">{{ $t("promotion_variant_hint_title") }}:</span>
                          <span
                            v-for="(variantLabel, idx) in item.variantLabels"
                            :key="`buy-${item.product_id}-variant-${idx}`"
                            class="item-variant-chip"
                          >
                            {{ variantLabel }}
                          </span>
                        </div>
                      </div>
                      <div class="item-qty-actions">
                        <v-text-field
                          v-model.number="item.qty"
                          type="number"
                          min="1"
                          dense
                          outlined
                          hide-details
                          class="item-qty-input"
                          @keydown="blockNonDigitKey"
                          @blur="clampQty(item)"
                        />
                        <v-btn icon small @click="removeBuyItem(item)">
                          <v-icon small color="error">fal fa-trash-alt</v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Get Products Section -->
              <div ref="field_get_items">
                <div class="section-label">{{ $t("get_products") }}</div>
                <div class="participant-box">
                  <v-btn
                    depressed
                    outlined
                    color="primary"
                    class="rounded-lg text-none participant-select-btn"
                    @click="getDialog = true"
                  >
                    <v-icon left small>fal fa-gift</v-icon>
                    {{ $t("select_products") }}
                  </v-btn>

                  <div v-if="fieldError('get_items').length" class="error-text mt-2">
                    {{ fieldError("get_items")[0] }}
                  </div>

                  <div v-if="form.get_items.length" class="item-qty-list mt-3">
                    <div
                      v-for="item in form.get_items"
                      :key="`get-${item.product_id}`"
                      class="item-qty-row"
                    >
                      <div class="item-qty-label-col">
                        <span class="item-qty-label font-weight-medium">{{ item.label }}</span>
                        <div v-if="item.variantLabels && item.variantLabels.length" class="item-variant-chips">
                          <span class="item-variant-chips-title">{{ $t("promotion_variant_hint_title") }}:</span>
                          <span
                            v-for="(variantLabel, idx) in item.variantLabels"
                            :key="`get-${item.product_id}-variant-${idx}`"
                            class="item-variant-chip"
                          >
                            {{ variantLabel }}
                          </span>
                        </div>
                      </div>
                      <div class="item-qty-actions">
                        <v-text-field
                          v-model.number="item.qty"
                          type="number"
                          min="1"
                          dense
                          outlined
                          hide-details
                          class="item-qty-input"
                          @keydown="blockNonDigitKey"
                          @blur="clampQty(item)"
                        />
                        <v-btn icon small @click="removeGetItem(item)">
                          <v-icon small color="error">fal fa-trash-alt</v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- 3. VALIDITY & ADVANCED SCHEDULE -->
        <v-card class="ap-card mb-4" outlined elevation="0">
          <v-card-title class="py-3 px-4 d-flex align-center border-bottom ap-card-title">
            <span class="ap-card-accent mr-2"></span>
            <span class="font-weight-bold text-subtitle-2">{{ $t("schedule_and_validity") || "Schedule & Validity" }}</span>
          </v-card-title>
          <v-card-text class="pa-4 pa-md-5">
            <div ref="field_date_range" class="mb-4">
              <div class="section-label">{{ $t("expired_date_optional") }}</div>
              <div class="schedule-date-row">
                <DatePicker
                  :placeholder="false"
                  :min="todayDate"
                  :max="form.end_date"
                  format="dd/mm/yyyy"
                  label="start_date"
                  :errorMessages="fieldError('start_date')"
                  v-model="form.start_date"
                  class="schedule-date-input"
                />
                <DatePicker
                  :placeholder="false"
                  :min="form.start_date || todayDate"
                  format="dd/mm/yyyy"
                  label="end_date"
                  :errorMessages="fieldError('end_date')"
                  v-model="form.end_date"
                  class="schedule-date-input"
                />
                <v-btn
                  v-if="form.start_date || form.end_date"
                  icon
                  small
                  class="schedule-clear-btn"
                  :title="$t('remove')"
                  @click="clearScheduleDates"
                >
                  <v-icon small color="error">fal fa-trash-alt</v-icon>
                </v-btn>
              </div>
              <div v-if="fieldError('date_range').length" class="error-text">
                {{ fieldError("date_range")[0] }}
              </div>
            </div>

            <!-- Advanced Settings Accordion -->
            <div>
              <button
                type="button"
                class="advanced-settings-toggle"
                @click="advancedSettingsOpen = !advancedSettingsOpen"
              >
                <div class="advanced-settings-toggle-title">
                  <v-icon small color="primary" class="mr-2">fal fa-sliders-h</v-icon>
                  <span>{{ $t("advanced_settings") }}</span>
                  <span class="advanced-settings-subtitle">
                    {{ $t("advanced_settings_subtitle") }}
                  </span>
                </div>
                <v-icon
                  small
                  class="advanced-settings-chevron"
                  :class="{ 'is-open': advancedSettingsOpen }"
                >
                  fal fa-chevron-down
                </v-icon>
              </button>

              <div
                class="advanced-settings-collapse"
                :class="{ 'is-open': advancedSettingsOpen }"
              >
                <div class="advanced-settings-body pt-4">
                  <div class="mb-4">
                    <div class="section-label">{{ $t("active_days") }}</div>
                    <div class="active-days-grid">
                      <button
                        v-for="day in activeDayOptions"
                        :key="day"
                        type="button"
                        class="day-toggle"
                        :class="{
                          active: form.active_days.includes(day),
                          disabled: !allowedActiveDays.includes(day),
                        }"
                        :disabled="!allowedActiveDays.includes(day)"
                        @click="toggleActiveDay(day)"
                      >
                        {{ $t(`day_${day}`) }}
                      </button>
                    </div>
                  </div>

                  <div ref="field_end_time">
                    <div class="section-label">{{ $t("active_time") }}</div>
                    <div class="schedule-date-row">
                      <TimePicker
                        :placeholder="$t('start_time')"
                        :errorMessages="fieldError('start_time')"
                        v-model="form.start_time"
                        class="schedule-date-input"
                      />
                      <TimePicker
                        :placeholder="$t('end_time')"
                        :errorMessages="fieldError('end_time')"
                        v-model="form.end_time"
                        class="schedule-date-input"
                      />
                      <v-btn
                        v-if="form.start_time || form.end_time"
                        icon
                        small
                        class="schedule-clear-btn"
                        :title="$t('remove')"
                        @click="clearScheduleTimes"
                      >
                        <v-icon small color="error">fal fa-trash-alt</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- ============ SIDEBAR COLUMN (4 COLS) ============ -->
      <v-col cols="12" lg="4" class="pl-lg-3">
        <!-- 1. STATUS & LIVE VOUCHER PREVIEW -->
        <v-card class="ap-card mb-4" outlined elevation="0">
          <v-card-title class="py-3 px-4 d-flex align-center justify-space-between border-bottom ap-card-title">
            <div class="d-flex align-center">
              <span class="ap-card-accent mr-2"></span>
              <span class="font-weight-bold text-subtitle-2">{{ $t("status_and_preview") || "Status & Preview" }}</span>
            </div>
            <v-chip x-small label :color="form.is_active ? 'success' : 'grey'" class="font-weight-bold white--text">
              {{ form.is_active ? $t("active") : $t("inactive") }}
            </v-chip>
          </v-card-title>
          <v-card-text class="pa-4">
            <!-- Active toggle switch -->
            <div class="enable-switch-row mb-4 pa-3 rounded-lg bg-slate-50">
              <div>
                <div class="font-weight-bold text-body-2">{{ $t("enable_promotion") }}</div>
                <div class="text-caption grey--text">{{ form.is_active ? $t("promotion_ready_hint") || "Ready to apply on POS" : $t("promotion_draft_hint") || "Disabled/Draft mode" }}</div>
              </div>
              <v-switch
                v-model="activeSwitch"
                inset
                color="primary"
                hide-details
                class="enable-switch-control ma-0 pa-0"
              />
            </div>

            <!-- LIVE VOUCHER CARD -->
            <div class="ps-promo-voucher" :class="{ 'ps-promo-voucher--inactive': !form.is_active }">
              <div class="ps-promo-voucher__top">
                <div class="ps-promo-voucher__brand">
                  <v-icon x-small color="primary" class="mr-1">fal fa-badge-percent</v-icon>
                  <span>POSSMART PROMO</span>
                </div>
                <span class="ps-promo-voucher__type-tag">
                  {{ $t(form.promotion_type) }}
                </span>
              </div>

              <div class="ps-promo-voucher__value">
                {{ previewDiscountValueText }}
              </div>

              <div class="ps-promo-voucher__name text-truncate">
                {{ form.name || $t("enter_promotion_name") }}
              </div>

              <div class="ps-promo-voucher__divider">
                <span class="ps-promo-voucher__notch ps-promo-voucher__notch--left"></span>
                <span class="ps-promo-voucher__dash"></span>
                <span class="ps-promo-voucher__notch ps-promo-voucher__notch--right"></span>
              </div>

              <div class="ps-promo-voucher__code-box">
                <div class="d-flex flex-column">
                  <span class="text-caption grey--text" style="font-size: 10px !important;">{{ $t("coupon_code") || "COUPON CODE" }}</span>
                  <span class="ps-promo-voucher__code">{{ form.code || "PROMO-CODE" }}</span>
                </div>
                <v-icon small color="primary">fal fa-ticket-alt</v-icon>
              </div>

              <div class="ps-promo-voucher__meta mt-3">
                <div class="d-flex align-center text-caption grey--text">
                  <v-icon x-small class="mr-1" color="#94a3b8">fal fa-clock</v-icon>
                  <span>{{ previewDateRangeText }}</span>
                </div>
                <div v-if="form.usage_limit" class="text-caption grey--text mt-1">
                  <v-icon x-small class="mr-1" color="#94a3b8">fal fa-users</v-icon>
                  <span>{{ $t("limit") }}: {{ form.usage_limit }}</span>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- 2. PROMOTION IMAGE BANNER -->
        <v-card class="ap-card mb-4" outlined elevation="0">
          <v-card-title class="py-3 px-4 d-flex align-center border-bottom ap-card-title">
            <span class="ap-card-accent mr-2"></span>
            <span class="font-weight-bold text-subtitle-2">{{ $t("promotion_image") }}</span>
          </v-card-title>
          <v-card-text class="pa-4">
            <DragDropUpload
              :value="imageUploadValue"
              accept=".svg,image/svg+xml,image/png,image/jpg,image/jpeg"
              :max-size="2 * 1024 * 1024"
              :max-size-m-b="2"
              @input="handleImageInput"
              @file-removed="handleImageRemoved"
            />
            <div class="upload-hint mt-2">{{ $t("promotion_image_hint") }}</div>
          </v-card-text>
        </v-card>

        <!-- 3. USAGE RESTRICTIONS -->
        <v-card class="ap-card mb-4" outlined elevation="0">
          <v-card-title class="py-3 px-4 d-flex align-center border-bottom ap-card-title">
            <span class="ap-card-accent mr-2"></span>
            <span class="font-weight-bold text-subtitle-2">{{ $t("usage_limits") || "Usage Limits" }}</span>
          </v-card-title>
          <v-card-text class="pa-4">
            <div ref="field_usage_limit">
              <div class="section-label">{{ $t("usage_limit") }}</div>
              <v-text-field
                v-model.number="form.usage_limit"
                type="number"
                min="1"
                :placeholder="$t('usage_limit_placeholder')"
                outlined
                dense
                hide-details
                @keydown="blockNonDigitKey"
                @blur="clampUsageLimit"
              />
              <div class="text-caption grey--text mt-1">
                {{ $t("usage_limit_helper") || "Total number of times this promotion can be redeemed." }}
              </div>
              <div v-if="fieldError('usage_limit').length" class="error-text">
                {{ fieldError("usage_limit")[0] }}
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- SKELETON PLACEHOLDER -->
    <v-row v-else>
      <v-col cols="12" lg="8" class="pr-lg-3">
        <v-card outlined class="rounded-xl ap-card mb-4">
          <v-card-text class="pa-5">
            <div class="skeleton-block skeleton-label mb-3"></div>
            <div class="skeleton-block skeleton-field mb-4"></div>
            <div class="skeleton-block skeleton-label mb-3"></div>
            <div class="skeleton-block skeleton-field"></div>
          </v-card-text>
        </v-card>
        <v-card outlined class="rounded-xl ap-card mb-4">
          <v-card-text class="pa-5">
            <div class="skeleton-block skeleton-label mb-3"></div>
            <div class="skeleton-cards-row">
              <div v-for="n in 4" :key="`skeleton-card-${n}`" class="skeleton-block skeleton-card"></div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" lg="4" class="pl-lg-3">
        <v-card outlined class="rounded-xl ap-card mb-4">
          <v-card-text class="pa-5">
            <div class="skeleton-block skeleton-label mb-3"></div>
            <div class="skeleton-block skeleton-upload-area mb-4"></div>
            <div class="skeleton-block skeleton-line"></div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- BOTTOM ACTION BAR -->
    <div v-if="!pageLoading" class="promotion-form-actions-bar mt-5 pa-4 ap-card d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">
      <div class="d-flex align-center" style="gap: 10px;">
        <v-btn
          depressed
          outlined
          class="text-none rounded-lg font-weight-bold px-4"
          @click="goBack"
        >
          {{ $t("cancel") }}
        </v-btn>
        <v-btn
          v-if="isEdit"
          depressed
          outlined
          color="error"
          class="text-none rounded-lg font-weight-bold px-4"
          @click="deletePromotion"
        >
          <v-icon left small>fal fa-trash-alt</v-icon>
          {{ $t("delete_promotion") }}
        </v-btn>
      </div>
      <v-btn
        depressed
        color="primary"
        class="text-none rounded-lg font-weight-bold px-6 white--text form-action-btn-primary"
        :loading="submitting"
        @click="submitForm"
      >
        <v-icon left small>{{ isEdit ? "fal fa-save" : "fal fa-check" }}</v-icon>
        {{ isEdit ? $t("update_promotion") : $t("create_promotion") }}
      </v-btn>
    </div>

    <!-- DIALOGS & PICKERS -->
    <PromotionParticipantPicker
      v-model="participantDialog"
      :mode="participantMode"
      :selected-ids="currentSelectedIds"
      :selected-items="participantSummaryItems"
      @save="handleParticipantSave"
    />

    <PromotionParticipantPicker
      v-model="buyDialog"
      mode="product"
      :selected-ids="buyItemIds"
      :selected-items="buyItemSummary"
      @save="handleBuyItemsSave"
    />

    <PromotionParticipantPicker
      v-model="getDialog"
      mode="product"
      :selected-ids="getItemIds"
      :selected-items="getItemSummary"
      @save="handleGetItemsSave"
    />

    <!-- DELETE CONFIRMATION DIALOG -->
    <v-dialog v-model="deleteConfirmOpen" max-width="420" content-class="rounded-xl">
      <v-card class="pa-4">
        <v-card-title class="font-weight-bold px-0 pt-0">
          <v-icon color="error" left>fal fa-exclamation-triangle</v-icon>
          {{ $t("are_you_sure") }}
        </v-card-title>
        <v-card-text class="px-0 pb-4">
          {{ $t("want_to_delete") }}
        </v-card-text>
        <v-card-actions class="px-0 pb-0 d-flex justify-end" style="gap: 8px;">
          <v-btn
            depressed
            outlined
            class="text-none rounded-lg font-weight-bold px-4"
            @click="deleteConfirmOpen = false"
          >
            {{ $t("cancel") }}
          </v-btn>
          <v-btn
            depressed
            color="error"
            class="text-none white--text rounded-lg font-weight-bold px-4"
            @click="confirmDeletePromotion"
          >
            <v-icon left small>fal fa-trash-alt</v-icon>
            {{ $t("delete_promotion") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <app-loading :overlay="overlay" />
  </v-container>
</template>

    <app-loading :overlay="overlay" />
  </v-container>
</template>

<script>
import CurrencyInput from "@/components/CurrencyInput.vue";
import PromotionParticipantPicker from "@/components/promotions/PromotionParticipantPicker.vue";
import DatePicker from "@/components/global/DatePicker.vue";
import TimePicker from "@/components/global/TimePicker.vue";
import DragDropUpload from "@/components/global/DragDropUpload.vue";
import TTextField from "@/components/global/TextField.vue";
import TextArea from "@/components/global/TextArea.vue";
import { toast } from "@/controllers/alert";
import Help from "@/utils/helper";
import {
  ACTIVE_DAY_OPTIONS,
  buildPromotionFormData,
  buildPromotionPayload,
  createPromotionForm,
  mapPromotionToForm,
  targetTypeForPromotionType,
} from "@/utils/promotions";

export default {
  name: "PromotionFormPage",
  components: {
    CurrencyInput,
    DatePicker,
    TimePicker,
    DragDropUpload,
    PromotionParticipantPicker,
    TTextField,
    TextArea,
  },
  data() {
    return {
      overlay: false,
      pageLoading: false,
      submitting: false,
      advancedSettingsOpen: false,
      deleteConfirmOpen: false,
      participantDialog: false,
      buyDialog: false,
      getDialog: false,
      form: createPromotionForm(),
      formErrors: {},
      categoryItems: [],
      productItems: [],
      originalImagePath: null,
    };
  },
  computed: {
    promotionId() {
      return this.$route.params.id;
    },
    isEdit() {
      return !!this.promotionId;
    },
    todayDate() {
      const now = new Date();
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, "0");
      const dd = String(now.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    },
    activeDayOptions() {
      return ACTIVE_DAY_OPTIONS;
    },
    allowedActiveDays() {
      if (!this.form.start_date || !this.form.end_date) {
        return ACTIVE_DAY_OPTIONS;
      }

      const start = new Date(`${this.form.start_date}T00:00:00`);
      const end = new Date(`${this.form.end_date}T00:00:00`);
      if (
        Number.isNaN(start.getTime()) ||
        Number.isNaN(end.getTime()) ||
        start > end
      ) {
        return ACTIVE_DAY_OPTIONS;
      }

      const diffDays = Math.round((end - start) / 86400000);
      if (diffDays >= 6) {
        return ACTIVE_DAY_OPTIONS;
      }

      const dayCodesByJsIndex = [
        "sun",
        "mon",
        "tue",
        "wed",
        "thu",
        "fri",
        "sat",
      ];
      const allowed = new Set();
      let cursor = new Date(start);
      while (cursor <= end) {
        allowed.add(dayCodesByJsIndex[cursor.getDay()]);
        cursor = new Date(cursor.getTime() + 86400000);
      }

      return ACTIVE_DAY_OPTIONS.filter((day) => allowed.has(day));
    },
    promotionTypeOptions() {
      return [
        {
          value: "bill_discount",
          icon: "fal fa-shopping-cart",
          descKey: "bill_discount_desc",
          isNew: false,
        },
        {
          value: "item_discount",
          icon: "fal fa-utensils",
          descKey: "item_discount_desc",
          isNew: true,
        },
        {
          value: "category_discount",
          icon: "fal fa-folder-open",
          descKey: "category_discount_desc",
          isNew: true,
        },
        {
          value: "buyx_gety",
          icon: "fal fa-gift",
          descKey: "buyx_gety_desc",
          isNew: true,
        },
      ];
    },
    usedOptions() {
      return [
        { value: "config", labelKey: "promotion_config" },
        { value: "custom", labelKey: "promotion_custom" },
      ];
    },
    targetTypeLabel() {
      const map = {
        all: this.$t("all"),
        category: this.$t("category"),
        product: this.$t("product"),
      };
      return map[this.form.target_type] || map.all;
    },
    imageUploadValue() {
      return this.form.imageFile || this.resolvedImagePath;
    },
    resolvedImagePath() {
      if (!this.form.imagePath) return null;
      return Help.img(this.form.imagePath);
    },
    activeSwitch: {
      get() {
        return !!this.form.is_active;
      },
      set(value) {
        this.form.is_active = value ? 1 : 0;
      },
    },
    participantMode() {
      return this.form.target_type === "product" ? "product" : "category";
    },
    participantButtonLabel() {
      return this.form.target_type === "product"
        ? this.$t("select_products")
        : this.$t("select_categories");
    },
    participantSummaryItems() {
      return this.form.target_type === "product"
        ? this.productItems
        : this.categoryItems;
    },
    currentSelectedIds() {
      return this.form.target_type === "product"
        ? this.form.product_ids
        : this.form.category_ids;
    },
    participantError() {
      return this.fieldError("participants");
    },
    buyItemIds() {
      return this.form.buy_items.map((item) => item.product_id);
    },
    buyItemSummary() {
      return this.form.buy_items.map((item) => ({
        id: item.product_id,
        label: item.label,
        variantIds: Array.isArray(item.variant_ids) ? item.variant_ids : [],
      }));
    },
    getItemIds() {
      return this.form.get_items.map((item) => item.product_id);
    },
    getItemSummary() {
      return this.form.get_items.map((item) => ({
        id: item.product_id,
        label: item.label,
        variantIds: Array.isArray(item.variant_ids) ? item.variant_ids : [],
      }));
    },
    discountTypeItems() {
      return [
        { text: "%", value: "percent" },
        { text: this.$t("amount"), value: "amount" },
      ];
    },
    percentDisplayValue() {
      return this.form.discount_value === null ? "" : String(this.form.discount_value);
    },
    previewDiscountValueText() {
      if (this.form.promotion_type === "buyx_gety") {
        return this.$t("buyx_gety") || "Buy X Get Y";
      }
      if (this.form.discount_type === "percent") {
        return `${this.form.discount_value || 0}% OFF`;
      }
      const val = Number(this.form.discount_value) || 0;
      return `${val.toLocaleString()} ₭ OFF`;
    },
    previewDateRangeText() {
      if (this.form.start_date && this.form.end_date) {
        return `${this.form.start_date} ~ ${this.form.end_date}`;
      }
      if (this.form.start_date) {
        return `From ${this.form.start_date}`;
      }
      return this.$t("always_active") || "Always Active";
    },
  },
  watch: {
    "$route.params.id": {
      immediate: false,
      handler() {
        this.initializePage();
      },
    },
    "form.name"(){
      this.clearFieldError("name");
    },
    "form.code"(){
      this.clearFieldError("code");
    },
    "form.discount_type"(value) {
      if (value === "percent" && this.form.discount_value > 100) {
        this.form.discount_value = 100;
      }
      if (this.form.discount_value < 0) {
        this.form.discount_value = 0;
      }
    },
    allowedActiveDays(allowed) {
      this.form.active_days = this.form.active_days.filter((day) =>
        allowed.includes(day)
      );
    },
  },
  created() {
    this.initializePage();
  },
  methods: {
    async initializePage() {
      this.formErrors = {};
      this.categoryItems = [];
      this.productItems = [];
      this.form = createPromotionForm();
      this.originalImagePath = null;

      if (!this.isEdit) {
        return;
      }

      this.pageLoading = true;
      try {
        const { data } = await this.$http.get(`promotions/${this.promotionId}`);
        if (data?.status) {
          const mapped = mapPromotionToForm(data.data, this.$i18n.locale);
          this.form = mapped.form;
          this.categoryItems = mapped.categoryItems;
          this.productItems = mapped.productItems;
          this.originalImagePath = mapped.form.imagePath;
        }
      } catch (error) {
        console.error("Failed to load promotion", error);
        await toast(this, "data_not_found", "error");
        this.goBack();
      } finally {
        this.pageLoading = false;
      }
    },
    fieldError(key) {
      const errors = this.formErrors[key];
      return Array.isArray(errors) ? errors : [];
    },
    clearFieldError(key) {
      if(!this.formErrors[key]) return;
      const updated = {...this.formErrors};
      delete updated[key];
      this.formErrors = updated;
    },
    translateBackendErrors(errors) {
      const knownMessages = [
        { pattern: /code.*already.*taken/i, key: "promotion_code_duplicate" },
      ];
      const translated = {};
      Object.keys(errors).forEach((field) => {
        translated[field] = (errors[field] || []).map((message) => {
          const match = knownMessages.find((entry) => entry.pattern.test(message));
          return match ? this.$t(match.key) : message;
        });
      });
      return translated;
    },
    scrollToFirstError() {
      const order = [
        "name",
        "code",
        "participants",
        "buy_items",
        "get_items",
        "discount_value",
        "usage_limit",
        "date_range",
        "start_date",
        "end_date",
        "end_time",
        "start_time",
      ];
      const key = order.find(
        (k) => this.formErrors[k] && this.formErrors[k].length
      );
      if (!key) return;

      const refMap = {
        start_date: "field_date_range",
        end_date: "field_date_range",
        start_time: "field_end_time",
        end_time: "field_end_time",
      };
      const refName = refMap[key] || `field_${key}`;
      const needsAdvancedOpen = refName === "field_end_time";

      if (needsAdvancedOpen) {
        this.advancedSettingsOpen = true;
      }

      this.$nextTick(() => {
        setTimeout(
          () => {
            const el = this.$refs[refName];
            const target = Array.isArray(el) ? el[0] : el;
            if (target && target.scrollIntoView) {
              target.scrollIntoView({ behavior: "smooth", block: "center" });
            }
          },
          needsAdvancedOpen ? 320 : 0
        );
      });
    },
    handleImageInput(value) {
      if (value instanceof File) {
        this.form.imageFile = value;
        this.form.imagePath = null;
        return;
      }
      this.form.imageFile = null;
      this.form.imagePath = value || null;
    },
    handleImageRemoved() {
      if (this.isEdit && this.originalImagePath) {
        this.form.imageFile = null;
        this.form.imagePath = this.originalImagePath;
        return;
      }
      this.form.imageFile = null;
      this.form.imagePath = null;
    },
    handlePromotionTypeChange(value) {
      const previousType = this.form.promotion_type;
      this.form.promotion_type = value;

      if (value === "buyx_gety") {
        this.form.discount_type = "percent";
        this.form.discount_value = null;
        if (previousType !== "buyx_gety") {
          this.form.product_ids = [];
          this.productItems = [];
        }
      } else if (previousType === "buyx_gety") {
        this.form.buy_items = [];
        this.form.get_items = [];
      }

      const targetType = targetTypeForPromotionType(value);
      const previousTargetType = this.form.target_type;
      this.form.target_type = targetType;

      if (targetType === previousTargetType) {
        return;
      }

      if (targetType === "all") {
        this.clearParticipants();
        return;
      }

      if (targetType === "category") {
        this.form.product_ids = [];
        this.productItems = [];
        return;
      }

      this.form.category_ids = [];
      this.categoryItems = [];
    },
    handlePercentInput(value) {
      const digits = String(value).replace(/\D/g, "");
      if (digits === "") {
        this.form.discount_value = null;
        return;
      }
      let num = Number.parseInt(digits, 10);
      if (num > 100) num = 100;
      if (num < 0) num = 0;
      this.form.discount_value = num;
    },
    clampQty(item) {
      const num = Number.parseInt(item.qty, 10);
      item.qty = !Number.isFinite(num) || num < 1 ? 1 : num;
    },
    clampUsageLimit() {
      if (this.form.usage_limit === null || this.form.usage_limit === "") {
        return;
      }
      const num = Number.parseInt(this.form.usage_limit, 10);
      this.form.usage_limit = !Number.isFinite(num) || num < 1 ? null : num;
    },
    blockNonDigitKey(event) {
      const allowedKeys = [
        "Backspace",
        "Delete",
        "Tab",
        "Escape",
        "Enter",
        "Home",
        "End",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
      ];
      if (allowedKeys.includes(event.key)) return;
      if (event.ctrlKey || event.metaKey) return;
      if (!/^\d$/.test(event.key)) {
        event.preventDefault();
      }
    },
    handlePercentPaste(event) {
      event.preventDefault();
      const pasted = (event.clipboardData || window.clipboardData).getData("text");
      this.handlePercentInput(pasted);
    },
    toggleActiveDay(day) {
      if (!this.allowedActiveDays.includes(day)) return;

      const index = this.form.active_days.indexOf(day);
      if (index === -1) {
        this.form.active_days = [...this.form.active_days, day];
        return;
      }
      this.form.active_days = this.form.active_days.filter((d) => d !== day);
    },
    clearParticipants() {
      this.form.category_ids = [];
      this.form.product_ids = [];
      this.categoryItems = [];
      this.productItems = [];
    },
    removeParticipantItem(item) {
      if (this.form.target_type === "product") {
        this.form.product_ids = this.form.product_ids.filter((id) => id !== item.id);
        this.productItems = this.productItems.filter((i) => i.id !== item.id);
        return;
      }
      this.form.category_ids = this.form.category_ids.filter((id) => id !== item.id);
      this.categoryItems = this.categoryItems.filter((i) => i.id !== item.id);
    },
    clearScheduleDates() {
      this.form.start_date = "";
      this.form.end_date = "";
    },
    clearScheduleTimes() {
      this.form.start_time = "";
      this.form.end_time = "";
    },
    handleParticipantSave({ ids, items }) {
      if (this.form.target_type === "product") {
        this.form.product_ids = [...ids];
        this.productItems = items;
        if (ids.length) this.clearFieldError("participants");
        return;
      }

      this.form.category_ids = [...ids];
      this.categoryItems = items;
      if (ids.length) this.clearFieldError("participants");
    },
    handleBuyItemsSave({ items }) {
      this.form.buy_items = items.map((item) => {
        const existing = this.form.buy_items.find((i) => i.product_id === item.id);
        return {
          product_id: item.id,
          label: item.label,
          qty: existing ? existing.qty : 1,
          variant_ids: Array.isArray(item.variantIds) ? item.variantIds : [],
          variantLabels: this.buildVariantLabels(item),
        };
      });
      if (this.form.buy_items.length) this.clearFieldError("buy_items");
    },
    handleGetItemsSave({ items }) {
      this.form.get_items = items.map((item) => {
        const existing = this.form.get_items.find((i) => i.product_id === item.id);
        return {
          product_id: item.id,
          label: item.label,
          qty: existing ? existing.qty : 1,
          variant_ids: Array.isArray(item.variantIds) ? item.variantIds : [],
          variantLabels: this.buildVariantLabels(item),
        };
      });
      if (this.form.get_items.length) this.clearFieldError("get_items");
    },
    buildVariantLabels(item) {
      if (!Array.isArray(item.variantIds) || !item.variantIds.length) return [];
      if (!Array.isArray(item.variants)) return [];
      return item.variants
        .filter((variant) => item.variantIds.includes(variant.id))
        .map((variant) => variant.label);
    },
    removeBuyItem(item) {
      this.form.buy_items = this.form.buy_items.filter(
        (i) => i.product_id !== item.product_id
      );
    },
    removeGetItem(item) {
      this.form.get_items = this.form.get_items.filter(
        (i) => i.product_id !== item.product_id
      );
    },
    validateNameAndCode(errors) {
      if (!this.form.name || !this.form.name.trim()) {
        errors.name = [this.$t("promotion_name_required")];
      } else if (this.form.name.trim().length > 150) {
        errors.name = [this.$t("promotion_name_maxlength")];
      }

      if (!this.form.code || !this.form.code.trim()) {
        errors.code = [this.$t("promotion_code_required")];
      } else if (this.form.code.trim().length > 15) {
        errors.code = [this.$t("promotion_code_maxlength")];
      }
    },
    validateDiscount(errors) {
      if (this.form.promotion_type === "buyx_gety") return;

      if (
        this.form.discount_value === null ||
        this.form.discount_value === "" ||
        Number(this.form.discount_value) < 0
      ) {
        errors.discount_value = [this.$t("discount_value_required")];
      }
      if (
        this.form.discount_type === "percent" &&
        Number(this.form.discount_value) > 100
      ) {
        errors.discount_value = [this.$t("percent_discount_max")];
      }
    },
    validateScheduleTimes(errors) {
      if (
        this.form.start_time &&
        this.form.end_time &&
        this.form.start_time >= this.form.end_time
      ) {
        errors.end_time = [this.$t("end_time_after_start_time")];
      }
    },
    validateBuyGetParticipants(errors) {
      if (!this.form.buy_items || !this.form.buy_items.length) {
        errors.buy_items = [this.$t("please_select_product")];
      }
      if (!this.form.get_items || !this.form.get_items.length) {
        errors.get_items = [this.$t("please_select_product")];
      }
    },
    validateTargetParticipants(errors) {
      if (
        this.form.target_type === "category" &&
        (!this.form.category_ids || !this.form.category_ids.length)
      ) {
        errors.participants = [this.$t("please_select_category")];
      }

      if (
        this.form.target_type === "product" &&
        (!this.form.product_ids || !this.form.product_ids.length)
      ) {
        errors.participants = [this.$t("please_select_product")];
      }
    },
    validateParticipants(errors) {
      if (this.form.promotion_type === "buyx_gety") {
        this.validateBuyGetParticipants(errors);
      } else {
        this.validateTargetParticipants(errors);
      }
    },
    validateDateRange(errors) {
      if (
        this.form.start_date &&
        this.form.end_date &&
        this.form.start_date > this.form.end_date
      ) {
        errors.date_range = [this.$t("promotion_end_date_after_start")];
      }
    },
    validateForm() {
      const errors = {};

      this.validateNameAndCode(errors);
      this.validateDiscount(errors);
      this.validateScheduleTimes(errors);
      this.validateParticipants(errors);
      this.validateDateRange(errors);

      this.formErrors = errors;
      return !Object.keys(errors).length;
    },
    async submitForm() {
      if (!this.validateForm()) {
        this.scrollToFirstError();
        return;
      }

      this.submitting = true;
      this.overlay = true;
      try {
        const isFormData = !!this.form.imageFile;
        const payload = isFormData
          ? buildPromotionFormData(this.form, this.productItems)
          : buildPromotionPayload(this.form, this.productItems);
        const config = isFormData
          ? { headers: { "Content-Type": "multipart/form-data" }, skipErrorAlert: true }
          : { skipErrorAlert: true };

        let request;
        if (this.isEdit) {
          // PHP doesn't parse multipart/form-data bodies on PUT requests, so
          // when an image file is attached we POST with _method=PUT spoofing
          // (Laravel routes it to update() and the fields parse correctly).
          if (isFormData) payload.append("_method", "PUT");
          request = isFormData
            ? this.$http.post(`promotions/${this.promotionId}`, payload, config)
            : this.$http.put(`promotions/${this.promotionId}`, payload, config);
        } else {
          request = this.$http.post("promotions", payload, config);
        }

        const { data } = await request;
        if (data?.status) {
          await toast(this, data.message || "save_promotion_success", "success");
          this.$router.push("/manage-promotions");
        }
      } catch (error) {
        const message = error?.response?.data?.message ?? error?.message;
        if (message && typeof message === "object") {
          this.formErrors = this.translateBackendErrors(message);
          this.scrollToFirstError();
        } else {
          await toast(this, message || "validation_required", "error");
        }
      } finally {
        this.submitting = false;
        this.overlay = false;
      }
    },
    deletePromotion() {
      if (!this.isEdit) return;
      this.deleteConfirmOpen = true;
    },
    async confirmDeletePromotion() {
      this.deleteConfirmOpen = false;
      this.overlay = true;
      try {
        const { data } = await this.$http.delete(`promotions/${this.promotionId}`, {
          skipErrorAlert: true,
        });
        if (data?.status) {
          await toast(this, data.message || "delete_promotion_success", "success");
          this.$router.push("/manage-promotions");
        }
      } finally {
        this.overlay = false;
      }
    },
    goBack() {
      this.$router.push("/manage-promotions");
    },
  },
};
</script>

<style scoped>
.promotion-form-page {
  padding-bottom: 40px;
}

/* Header & Back Button */
.ap-header-row {
  margin-bottom: 24px;
}

.ap-back-btn {
  min-width: 42px !important;
  width: 42px;
  height: 42px !important;
  padding: 0 !important;
  border: 1px solid #e2e8f0 !important;
  background: #ffffff !important;
  color: var(--app-primary, #5b39c9) !important;
  transition: all 0.15s ease;
}

.ap-back-btn:hover {
  background: rgba(91, 57, 201, 0.06) !important;
  border-color: var(--app-primary, #5b39c9) !important;
}

.theme--dark .ap-back-btn {
  background: #1e1e2d !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
}

.promotion-form-title {
  font-size: 24px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.02em;
}

.theme--dark .promotion-form-title {
  color: #ffffff;
}

/* PosSmart Card & Section Styles */
.ap-card {
  border-radius: 16px !important;
  border: 1px solid #e2e8f0 !important;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02) !important;
  overflow: hidden;
}

.theme--dark .ap-card {
  background: #1e1e2d !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.ap-card-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  background: #ffffff;
}

.theme--dark .ap-card-title {
  background: #1e1e2d !important;
  color: #ffffff !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.ap-card-accent {
  width: 3px;
  height: 15px;
  border-radius: 2px;
  background: var(--app-primary, #5b39c9);
  display: inline-block;
}

.section-label {
  font-size: 13.5px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
}

.theme--dark .section-label {
  color: rgba(255, 255, 255, 0.88);
}

.section-block {
  margin-top: 22px;
}

.required-mark {
  color: #ef4444;
  font-weight: 700;
}

.upload-hint {
  color: #94a3b8;
  font-size: 12px;
}

/* Form Inputs Grid */
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
}

/* Input Fields Appearance */
.ap-card ::v-deep .v-text-field--outlined .v-input__slot {
  background: #f8fafc !important;
  border-radius: 12px !important;
  min-height: 44px;
  border: 1px solid #e2e8f0 !important;
}

.ap-card ::v-deep .v-text-field--outlined fieldset {
  border: none !important;
}

.ap-card ::v-deep .v-text-field--outlined.v-input--is-focused .v-input__slot {
  background: #ffffff !important;
  border-color: var(--app-primary, #5b39c9) !important;
  box-shadow: 0 0 0 3px rgba(91, 57, 201, 0.12) !important;
}

.theme--dark .ap-card ::v-deep .v-text-field--outlined .v-input__slot {
  background: #262638 !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
}

.theme--dark .ap-card ::v-deep .v-text-field--outlined.v-input--is-focused .v-input__slot {
  border-color: #8b6ff0 !important;
  box-shadow: 0 0 0 3px rgba(139, 111, 240, 0.2) !important;
}

/* Promotion Type 4-Card Selector Grid */
.promotion-type-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

@media (max-width: 960px) {
  .promotion-type-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 480px) {
  .promotion-type-grid {
    grid-template-columns: 1fr;
  }
}

.promotion-type-card {
  position: relative;
  text-align: left;
  padding: 16px 14px;
  border: 1.5px solid #e2e8f0;
  border-radius: 14px;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.18s cubic-bezier(0.16, 1, 0.3, 1);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.promotion-type-card:hover {
  border-color: #cbd5e1;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.promotion-type-card.active {
  border-color: var(--app-primary, #5b39c9) !important;
  background: rgba(91, 57, 201, 0.04) !important;
  box-shadow: 0 4px 16px rgba(91, 57, 201, 0.12) !important;
}

.theme--dark .promotion-type-card {
  background: #262638;
  border-color: rgba(255, 255, 255, 0.12);
}

.theme--dark .promotion-type-card:hover {
  border-color: rgba(255, 255, 255, 0.24);
}

.theme--dark .promotion-type-card.active {
  background: rgba(139, 111, 240, 0.12) !important;
  border-color: #8b6ff0 !important;
}

.promotion-type-icon-wrap {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  transition: all 0.18s ease;
}

.promotion-type-icon {
  color: #64748b;
}

.promotion-type-card.active .promotion-type-icon-wrap {
  background: var(--app-primary, #5b39c9);
}

.promotion-type-card.active .promotion-type-icon {
  color: #ffffff !important;
}

.promotion-type-title {
  font-size: 13.5px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.3;
}

.theme--dark .promotion-type-title {
  color: #ffffff;
}

.promotion-type-subtitle {
  margin-top: 4px;
  font-size: 11.5px;
  color: #94a3b8;
  line-height: 1.4;
}

.promotion-type-check {
  position: absolute;
  top: 10px;
  right: 10px;
  color: var(--app-primary, #5b39c9) !important;
}

/* Discount Value & Currency Unified Input */
.discount-value-row {
  display: flex;
  align-items: stretch;
  max-width: 360px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
  overflow: hidden;
  transition: all 0.18s ease;
}

.discount-value-row:focus-within {
  border-color: var(--app-primary, #5b39c9);
  background: #ffffff;
  box-shadow: 0 0 0 3px rgba(91, 57, 201, 0.12);
}

.theme--dark .discount-value-row {
  background: #262638;
  border-color: rgba(255, 255, 255, 0.12);
}

.theme--dark .discount-value-row:focus-within {
  border-color: #8b6ff0;
  box-shadow: 0 0 0 3px rgba(139, 111, 240, 0.2);
}

.discount-value-input {
  flex: 1;
  min-width: 0;
}

.discount-type-select {
  width: 120px;
  flex: none;
}

.discount-value-input >>> .v-input__slot,
.discount-type-select >>> .v-input__slot {
  box-shadow: none !important;
  background: transparent !important;
  min-height: 44px;
  padding: 0 14px !important;
}

.discount-value-currency >>> fieldset {
  border: none !important;
}

.discount-value-currency >>> .v-text-field__details {
  display: none !important;
}

.discount-divider {
  width: 1px;
  margin: 6px 0;
  background: #e2e8f0;
}

.theme--dark .discount-divider {
  background: rgba(255, 255, 255, 0.12);
}

.discount-type-select >>> .v-select__selection {
  font-weight: 700;
  color: #0f172a;
}

.theme--dark .discount-type-select >>> .v-select__selection {
  color: #ffffff;
}

.discount-type-check-icon {
  min-width: 20px !important;
  margin-right: 8px !important;
}

/* Participant Target Box */
.participant-box {
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #f8fafc;
}

.theme--dark .participant-box {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.08);
}

.participant-targets {
  display: flex;
  align-items: center;
}

.participant-target-label {
  font-size: 13.5px;
  font-weight: 700;
  color: #0f172a;
}

.theme--dark .participant-target-label {
  color: #ffffff;
}

.participant-select-btn {
  height: 38px !important;
  padding: 0 18px !important;
  font-weight: 700;
  background: #ffffff !important;
}

.theme--dark .participant-select-btn {
  background: #262638 !important;
  border-color: rgba(255, 255, 255, 0.16) !important;
}

.selected-chips {
  margin-top: 12px;
}

/* Schedule / Date Picker Row */
.schedule-date-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.schedule-date-input {
  flex: 1;
  min-width: 0;
}

.schedule-clear-btn {
  flex: none;
  margin-top: 10px;
}

/* Advanced Settings Accordion */
.advanced-settings-toggle {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
  cursor: pointer;
  color: #0f172a;
  transition: all 0.18s ease;
}

.advanced-settings-toggle:hover {
  border-color: var(--app-primary, #5b39c9);
  background: rgba(91, 57, 201, 0.04);
}

.theme--dark .advanced-settings-toggle {
  background: #262638;
  border-color: rgba(255, 255, 255, 0.12);
  color: #ffffff;
}

.theme--dark .advanced-settings-toggle:hover {
  background: rgba(139, 111, 240, 0.12);
  border-color: #8b6ff0;
}

.advanced-settings-toggle-title {
  display: flex;
  align-items: center;
  font-size: 13.5px;
  font-weight: 700;
}

.advanced-settings-subtitle {
  margin-left: 10px;
  font-size: 12px;
  font-weight: 400;
  color: #94a3b8;
}

.advanced-settings-collapse {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.25s ease;
}

.advanced-settings-collapse.is-open {
  grid-template-rows: 1fr;
}

.advanced-settings-body {
  overflow: hidden;
  min-height: 0;
}

.advanced-settings-chevron {
  transition: transform 0.2s ease;
}

.advanced-settings-chevron.is-open {
  transform: rotate(180deg);
}

/* Active Day Pills */
.active-days-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.day-toggle {
  height: 36px;
  padding: 0 16px;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #475569;
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  transition: all 0.15s ease;
}

.day-toggle:hover {
  border-color: var(--app-primary, #5b39c9);
}

.day-toggle.active {
  background: var(--app-primary, #5b39c9) !important;
  border-color: var(--app-primary, #5b39c9) !important;
  color: #ffffff !important;
  box-shadow: 0 2px 8px rgba(91, 57, 201, 0.25);
}

.theme--dark .day-toggle {
  background: #262638;
  border-color: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.7);
}

.theme--dark .day-toggle.active {
  background: #8b6ff0 !important;
  border-color: #8b6ff0 !important;
  color: #ffffff !important;
}

.day-toggle.disabled {
  cursor: not-allowed;
  opacity: 0.4;
}

/* Sidebar Live Voucher Card */
.ps-promo-voucher {
  position: relative;
  padding: 18px;
  border-radius: 16px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 1.5px dashed #cbd5e1;
  overflow: hidden;
  transition: all 0.2s ease;
}

.theme--dark .ps-promo-voucher {
  background: linear-gradient(135deg, #262638 0%, #1e1e2d 100%);
  border-color: rgba(255, 255, 255, 0.16);
}

.ps-promo-voucher--inactive {
  opacity: 0.65;
  filter: grayscale(0.5);
}

.ps-promo-voucher__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.ps-promo-voucher__brand {
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.05em;
  color: var(--app-primary, #5b39c9);
  display: flex;
  align-items: center;
}

.ps-promo-voucher__type-tag {
  font-size: 10.5px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 6px;
  background: rgba(91, 57, 201, 0.08);
  color: var(--app-primary, #5b39c9);
}

.ps-promo-voucher__value {
  font-size: 26px;
  font-weight: 900;
  color: #0f172a;
  letter-spacing: -0.02em;
  line-height: 1.1;
}

.theme--dark .ps-promo-voucher__value {
  color: #ffffff;
}

.ps-promo-voucher__name {
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  margin-top: 4px;
  margin-bottom: 14px;
}

.ps-promo-voucher__divider {
  position: relative;
  height: 1px;
  margin: 12px -18px;
  display: flex;
  align-items: center;
}

.ps-promo-voucher__dash {
  flex: 1;
  border-top: 1.5px dashed #cbd5e1;
}

.theme--dark .ps-promo-voucher__dash {
  border-color: rgba(255, 255, 255, 0.16);
}

.ps-promo-voucher__notch {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
}

.theme--dark .ps-promo-voucher__notch {
  background: #1e1e2d;
  border-color: rgba(255, 255, 255, 0.16);
}

.ps-promo-voucher__notch--left {
  left: -7px;
}

.ps-promo-voucher__notch--right {
  right: -7px;
}

.ps-promo-voucher__code-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  margin-top: 4px;
}

.theme--dark .ps-promo-voucher__code-box {
  background: #1e1e2d;
  border-color: rgba(255, 255, 255, 0.12);
}

.ps-promo-voucher__code {
  font-family: monospace;
  font-weight: 800;
  font-size: 15px;
  letter-spacing: 0.08em;
  color: #0f172a;
}

.theme--dark .ps-promo-voucher__code {
  color: #ffffff;
}

/* Buy X Get Y Item List */
.item-qty-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #f1f5f9;
}

.theme--dark .item-qty-row {
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

.item-qty-row:last-child {
  border-bottom: 0;
}

.item-qty-label-col {
  flex: 1;
  min-width: 0;
}

.item-qty-actions {
  flex: none;
  display: flex;
  align-items: center;
  gap: 8px;
}

.item-qty-input {
  max-width: 90px;
  flex: none;
}

.item-variant-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(91, 57, 201, 0.08);
  color: var(--app-primary, #5b39c9);
  font-size: 11px;
  font-weight: 600;
}

.theme--dark .item-variant-chip {
  background: rgba(139, 111, 240, 0.16);
  color: #8b6ff0;
}

/* Enable Switch Row */
.enable-switch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.bg-slate-50 {
  background: #f8fafc;
}

.theme--dark .bg-slate-50 {
  background: rgba(255, 255, 255, 0.04) !important;
}

/* Error text */
.error-text {
  color: #ef4444;
  font-size: 12px;
  margin-top: 4px;
}

/* Bottom Action Bar */
.promotion-form-actions-bar {
  border-radius: 16px !important;
  border: 1px solid #e2e8f0 !important;
  background: #ffffff;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04) !important;
}

.theme--dark .promotion-form-actions-bar {
  background: #1e1e2d !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.form-action-btn-primary {
  height: 44px !important;
  font-size: 14px;
  font-weight: 700;
}

/* Skeleton Loading */
.skeleton-block {
  border-radius: 8px;
  background: linear-gradient(90deg, #eef1f5 25%, #f7f9fb 37%, #eef1f5 63%);
  background-size: 400% 100%;
  animation: promotion-skeleton-shimmer 1.4s ease infinite;
}

@keyframes promotion-skeleton-shimmer {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}

.skeleton-label {
  width: 120px;
  height: 14px;
}

.skeleton-upload-area {
  width: 100%;
  height: 200px;
  border-radius: 14px;
}

.skeleton-line {
  width: 60%;
  height: 16px;
}

.skeleton-field {
  width: 100%;
  height: 44px;
  border-radius: 10px;
}

.skeleton-cards-row {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.skeleton-card {
  height: 100px;
  border-radius: 14px;
}

.theme--dark .skeleton-block {
  background: linear-gradient(90deg, #262626 25%, #333 37%, #262626 63%);
  background-size: 400% 100%;
}
</style>
